import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiClient, parseApiError } from "@/app/services/api";
import { type Lang } from "@/app/i18n/types";
import { useMlField } from "@/app/components/ui/ml-field";
import { stripEmpty, stripEmptyFeatures } from "@/app/utils/mlField";
import { ROUTES } from "@/app/config/routes";
import { MetadataCard, PackageUploadCard, ReleasePolicyCard } from "@/app/pages/upload/UploadPageSections";
import { extractLappMeta, type LappMeta } from "@/app/pages/upload/extract-lapp-meta";
import type { UploadResult } from "@/app/types/api";

export function UploadPage() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? "vi") as Lang;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [file, setFile] = React.useState<File | null>(null);
  const [publish, setPublish] = React.useState(true);
  const [upload, setUpload] = React.useState(true);
  const [force, setForce] = React.useState(false);
  const [packageMeta, setPackageMeta] = React.useState<LappMeta | null>(null);
  const [packageError, setPackageError] = React.useState<string | null>(null);
  const [isInspectingPackage, setIsInspectingPackage] = React.useState(false);
  const [vendor, setVendor] = React.useState("");
  const [descShort, setDescShort] = useMlField();
  const [desc, setDesc] = useMlField();
  const [features, setFeatures] = useMlField();
  const [changelog, setChangelog] = useMlField();
  const [depsText, setDepsText] = React.useState("");
  const [tags, setTags] = React.useState<string[]>([]);
  const [resultText, setResultText] = React.useState<string | null>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const jsonInputRef = React.useRef<HTMLInputElement>(null);

  const reset = () => {
    setFile(null);
    setPublish(true);
    setUpload(true);
    setForce(false);
    setPackageMeta(null);
    setPackageError(null);
    setIsInspectingPackage(false);
    setVendor("");
    setDescShort({});
    setDesc({});
    setFeatures({});
    setChangelog({});
    setDepsText("");
    setTags([]);
    setResultText(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const importJson = async (f: File) => {
    try {
      const obj = JSON.parse(await f.text());
      setVendor(obj.vendor ?? "");
      setDescShort(obj.description_short ?? {});
      setDesc(obj.description ?? {});
      setFeatures(obj.features ?? {});
      setChangelog(obj.changelog ?? {});
      setDepsText(Array.isArray(obj.dependencies) ? obj.dependencies.join("\n") : "");
      setTags(Array.isArray(obj.tags) ? obj.tags : []);
      toast.success(t("toast.jsonLoaded"));
    } catch (e) {
      toast.error(t("toast.jsonInvalid", { error: parseApiError(e) }));
    }
  };

  const selectFile = React.useCallback(async (next: File | null) => {
    setFile(next);
    setPackageMeta(null);
    setPackageError(null);
    if (!next) return;
    setIsInspectingPackage(true);
    try {
      const meta = await extractLappMeta(next);
      setPackageMeta(meta);
    } catch (e) {
      setPackageError(parseApiError(e));
    } finally {
      setIsInspectingPackage(false);
    }
  }, []);

  const submit = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error(t("toast.noFile"));
      const fd = new FormData();
      fd.append("file", file);
      fd.append("publish", String(publish));
      fd.append("upload", String(upload));
      fd.append("force", String(force));
      const res = await apiClient.post<UploadResult>(
        "/api/v1/admin/apps/upload",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return res.data;
    },
    onSuccess: async (res) => {
      const meta: Record<string, unknown> = {};
      if (vendor.trim()) meta.vendor = vendor.trim();
      const ds = stripEmpty(descShort);
      if (ds) meta.description_short = ds;
      const de = stripEmpty(desc);
      if (de) meta.description = de;
      const cl = stripEmpty(changelog);
      if (cl) meta.changelog = cl;
      const f = stripEmptyFeatures(features);
      if (f) meta.features = f;
      const d = depsText.split("\n").map((s) => s.trim()).filter(Boolean);
      if (d.length) meta.dependencies = d;
      if (tags.length) meta.tags = tags.slice();

      let metaSaved = false;
      let metaError: string | null = null;
      if (Object.keys(meta).length) {
        try {
          await apiClient.patch(
            `/api/v1/admin/apps/${encodeURIComponent(res.app_id)}`,
            meta
          );
          metaSaved = true;
        } catch (e) {
          metaError = parseApiError(e);
        }
      }
      setResultText(JSON.stringify(res, null, 2));
      const metaSuffix = metaSaved ? " + metadata" : "";
      if (metaError) {
        toast.error(
          t("toast.metaFail", { app: res.app_id, version: res.version, error: metaError })
        );
      } else {
        toast.success(
          t("toast.uploaded", { app: res.app_id, version: res.version, meta: metaSuffix })
        );
      }
      queryClient.invalidateQueries({ queryKey: ["admin", "apps"] });
      reset();
      navigate(ROUTES.appsDetail(res.app_id));
    },
    onError: (e) => {
      toast.error(parseApiError(e));
    },
  });

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) void selectFile(dropped);
  };

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="grid gap-3 xl:grid-cols-[minmax(0,3fr)_minmax(0,7fr)]">
        <PackageUploadCard
          file={file}
          packageMeta={packageMeta}
          packageError={packageError}
          isDragOver={isDragOver}
          fileInputRef={fileInputRef}
          setIsDragOver={setIsDragOver}
          onSelectFile={selectFile}
          onDrop={onDrop}
        />

        <ReleasePolicyCard
          publish={publish}
          upload={upload}
          force={force}
          onPublishChange={setPublish}
          onUploadChange={setUpload}
          onForceChange={setForce}
          file={file}
          isInspectingPackage={isInspectingPackage}
          packageMeta={packageMeta}
        />
      </div>

      <MetadataCard
        lang={lang}
        jsonInputRef={jsonInputRef}
        onImportJson={importJson}
        vendor={vendor}
        onVendorChange={setVendor}
        tags={tags}
        onTagsChange={setTags}
        descShort={descShort}
        onDescShortChange={setDescShort}
        desc={desc}
        onDescChange={setDesc}
        features={features}
        onFeaturesChange={setFeatures}
        changelog={changelog}
        onChangelogChange={setChangelog}
        depsText={depsText}
        onDepsTextChange={setDepsText}
        resultText={resultText}
        onReset={reset}
        onSubmit={() => submit.mutate()}
        isSubmitting={submit.isPending}
        canSubmit={!!file && !isInspectingPackage}
      />
    </div>
  );
}
